import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { t } from 'i18next'; // apis
import { deleteItem, undoDeleteProduct } from 'api';
// components
import { useToast } from '@commons/EXComponentsLibrary';
// utils && hooks
import { ProductInterface } from 'common/types';
import {
    disabledButtonMakeOrder,
    enabledButtonMakeOrder,
} from '@commons/components/TableComparison/utils/changeStateButtonMakeOrder';

type ProductRemove = {
    index: number;
    barcode: string;
    clicked: boolean;
    orderProductId: number;
};

type ContextProps = {
    products: ProductInterface[];
    setProducts: (products: ProductInterface[]) => void;
    setProductInfo: (product: ProductInterface) => void;
    cleanProducts: () => void;
    handleRemoveProduct: (information: ProductRemove) => void;
};

const defaultValues: ContextProps = {
    products: [],
    setProducts: (products: ProductInterface[]) => {},
    setProductInfo: (product: ProductInterface) => {},
    cleanProducts: () => {},
    handleRemoveProduct: (information: ProductRemove) => {},
};

type Props = {
    children: React.ReactNode;
};

const ProductsContext = createContext<ContextProps>(defaultValues);

export const ProductsProvider = ({ children }: Props) => {
    const {
        replace,
        query: { orderId },
    } = useRouter();

    const { showToaster, clearToaster } = useToast();

    const [products, setProducts] = useState<ProductInterface[]>(defaultValues.products);
    const productsRef: any = useRef(products);

    useEffect(() => {
        productsRef.current = products;
    }, [products]);

    const updateProduct = useCallback(
        (item: any) => {
            const { barcode, isGeneric, orderProductIdParent } = item;
            setProducts((prevState: any) =>
                prevState.map((product) => {
                    if (!!isGeneric && !!orderProductIdParent) {
                        // Encuentra el producto padre
                        if (product.order_product_id === orderProductIdParent) {
                            // Si es un genérico, busca el genérico correspondiente por barcode
                            const updatedGenerics = product.generics.map((generic: any) => {
                                if (generic.barcode === barcode) {
                                    return item; // Reemplaza el genérico por el item actualizado
                                }
                                return generic;
                            });

                            // Actualiza el producto con el nuevo genérico
                            return { ...product, generics: updatedGenerics };
                        }
                        return product;
                    } else {
                        // Si no es genérico, simplemente actualiza el producto por barcode
                        return product.barcode === barcode ? item : product;
                    }
                }),
            );
        },
        [products],
    );

    // función encargada de actualizar información de producto cuando sufre alguna actualización
    const setProductInfo = useCallback((item: any) => {
        const { isGeneric, orderProductIdParent, barcode } = item;
        const exists = productsRef.current?.some(({ order_product_id, barcode: productBarcode }) =>
            isGeneric && orderProductIdParent ? order_product_id === orderProductIdParent : productBarcode === barcode,
        );
        exists ? updateProduct(item) : setProducts((prev) => [...prev, item]);
    }, []);

    const cleanProducts = () => {
        setProducts((prevState) => []);
    };

    // función encargada de eliminar producto
    const handleRemoveProduct = useCallback((information: ProductRemove): void => {
        const currentProducts = productsRef.current;

        const handleUndo = async (information: any) => {
            enabledButtonMakeOrder();
            try {
                undoItem(information.barcode, information.index);
                await undoDeleteProduct(+orderId, information.orderProductId);
                clearToaster();
            } catch {
                clearToaster();
            }
        };

        const undoItem = async (barcode, index = 0) => {
            // get product
            let filteredProduct = currentProducts.find((e) => barcode == e.barcode);
            // search product in products
            let filteredProducts = currentProducts.filter((e) => filteredProduct.barcode !== e.barcode);
            // push in correct position
            filteredProducts.splice(index, 0, filteredProduct);
            // set states
            setProducts(filteredProducts);
        };

        const removeItemInState = async (information) => {
            const { barcode } = information;
            let filteredProduct = currentProducts.find((e) => barcode == e.barcode);

            let filteredProducts = currentProducts.filter((e) => filteredProduct.barcode !== e.barcode);

            setProducts(filteredProducts);
            const productId = filteredProduct.order_product_id;
            await deleteItem(orderId, productId);
            enabledButtonMakeOrder();
        };

        disabledButtonMakeOrder();
        if (currentProducts.length == 1) {
            replace('/efficient-purchase/');
            return;
        }

        removeItemInState(information);
        showToaster({
            message: {
                description: t('shoppingCart.shoppingCart_Thirteen'),
            },
            type: 'success',
            duration: 5000,
            actionButton: {
                label: t('efficientPurchasing.comparative.efficientPurchasing_comparativeFourteen'),
                action: () => handleUndo(information),
            },
        });
    }, []);

    const value: ContextProps = useMemo(
        () => ({
            products,
            setProducts,
            setProductInfo,
            cleanProducts,
            handleRemoveProduct,
        }),
        [products],
    );

    return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>;
};

export const useProductsContext = () => useContext(ProductsContext);
